/**
 * Central admin page registry - the single source of admin page metadata.
 *
 * Both the side nav (see `buildAdminNav`, landing with the nav refactor) and
 * the shared `AdminPageHeader` derive title, icon, description and breadcrumbs
 * from this file, so they cannot drift. Page-specific business logic stays in
 * the pages; this only holds presentation metadata + RBAC pointers.
 *
 * RBAC is NOT re-implemented here: `featureGate` / `requiredRoles` are pointers
 * consumed by the existing `RoleGuard` / `hasFeatureAccess()` in services/auth.
 */
import type { SvgIconComponent } from '@mui/icons-material'
import BusinessIcon from '@mui/icons-material/Business'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AssessmentIcon from '@mui/icons-material/Assessment'
import SyncIcon from '@mui/icons-material/Sync'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import LinkIcon from '@mui/icons-material/Link'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import SecurityIcon from '@mui/icons-material/Security'
import ConstructionIcon from '@mui/icons-material/Construction'
import SettingsIcon from '@mui/icons-material/Settings'
import PublicIcon from '@mui/icons-material/Public'
import GroupIcon from '@mui/icons-material/Group'
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DashboardIcon from '@mui/icons-material/Dashboard'
import GridViewIcon from '@mui/icons-material/GridView'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import FlagIcon from '@mui/icons-material/Flag'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import HistoryIcon from '@mui/icons-material/History'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import GroupsIcon from '@mui/icons-material/Groups'
import StorageIcon from '@mui/icons-material/Storage'
import ListAltIcon from '@mui/icons-material/ListAlt'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import HubIcon from '@mui/icons-material/Hub'

export type AdminPageKey =
  | 'franchisePartners'
  | 'systemConnections'
  | 'manageConnections'
  | 'feedback'
  | 'dataExceptions'
  | 'exceptionTypes'
  | 'healthScores'
  | 'platformReports'
  | 'platformReportDetail'
  | 'dataSync'
  | 'userManagement'
  | 'editUser'
  | 'systemStatus'
  | 'auditLogs'
  | 'userActivity'
  | 'adminTools'
  | 'bulkOperations'
  | 'pwxMasters'
  | 'bdm'
  | 'settings'
  | 'regionManagement'
  | 'integrations'
  | 'freshdeskIntegration'
  | 'confluenceIntegration'

export interface AdminPageDefinition {
  key: AdminPageKey
  /** Nav label + default page title. */
  label: string
  /** Canonical route (nav landing target for parent pages). */
  path: string
  /** Same icon used by the side nav and the page header tile. */
  icon: SvgIconComponent
  /** Default sub-text: sentence case, verb-first, 8-14 words. */
  description: string
  section: 'main' | 'system'
  /** Breadcrumb / nav parent (within `main`). System pages derive their
   *  "System" crumb from `section` instead. */
  parentKey?: AdminPageKey
  /** Existing feature key consumed by RoleGuard / hasFeatureAccess. */
  featureGate?: string
}

/** A single breadcrumb crumb. `to` present => clickable link; absent => current page. */
export interface BreadcrumbItem {
  label: string
  to?: string
}

export const adminPages: Record<AdminPageKey, AdminPageDefinition> = {
  franchisePartners: {
    key: 'franchisePartners',
    label: 'Franchise Partners',
    path: '/admin/franchise-partners',
    icon: BusinessIcon,
    description: 'Manage franchise partner records, lifecycle status and access.',
    section: 'main',
    featureGate: 'franchise_partners',
  },
  systemConnections: {
    key: 'systemConnections',
    label: 'System Connections',
    path: '/admin/system-connections',
    icon: LinkIcon,
    description: 'Monitor and manage system connections across franchise partners.',
    section: 'main',
    parentKey: 'franchisePartners',
    featureGate: 'franchise_partners',
  },
  manageConnections: {
    key: 'manageConnections',
    label: 'Manage Connections',
    path: '/admin/franchise-partners',
    icon: LinkIcon,
    description: 'Configure and monitor system connections for this franchise partner.',
    section: 'main',
    parentKey: 'franchisePartners',
    featureGate: 'franchise_partners',
  },
  feedback: {
    key: 'feedback',
    label: 'Feedback',
    path: '/admin/feedback',
    icon: FeedbackIcon,
    description: 'Review in-app feedback from Evo, PSC and franchise partners.',
    section: 'main',
    parentKey: 'franchisePartners',
    featureGate: 'franchise_partners',
  },
  dataExceptions: {
    key: 'dataExceptions',
    label: 'Data Exceptions',
    path: '/admin/data-exceptions',
    icon: FactCheckIcon,
    description: 'Triage and manage cross-franchise data quality exceptions.',
    section: 'main',
    featureGate: 'data_exceptions',
  },
  healthScores: {
    key: 'healthScores',
    label: 'Health Scores',
    path: '/admin/health-scores',
    icon: LocalHospitalIcon,
    description: 'Track franchise partner health scores and data quality trends.',
    section: 'main',
    featureGate: 'health_scores',
  },
  platformReports: {
    key: 'platformReports',
    label: 'Platform Reports',
    path: '/admin/platform-reports',
    icon: AssessmentIcon,
    description: 'Run cross-franchise reports and review export-ready results.',
    section: 'main',
    featureGate: 'platform_reports',
  },
  platformReportDetail: {
    key: 'platformReportDetail',
    label: 'Report Detail',
    path: '/admin/platform-reports',
    icon: AssessmentIcon,
    description: 'Configure report filters and generate matching results.',
    section: 'main',
    parentKey: 'platformReports',
    featureGate: 'platform_reports',
  },
  dataSync: {
    key: 'dataSync',
    label: 'Data Sync',
    path: '/admin/data-sync/active',
    icon: SyncIcon,
    description: 'Monitor active syncs, review history and trigger manual syncs.',
    section: 'main',
    featureGate: 'data_sync',
  },
  userManagement: {
    key: 'userManagement',
    label: 'User Management',
    path: '/admin/users',
    icon: PeopleIcon,
    description: 'Manage user roles, status and franchise partner access.',
    section: 'main',
    featureGate: 'user_management',
  },
  userActivity: {
    key: 'userActivity',
    label: 'User Activity',
    path: '/admin/users/activity',
    icon: QueryStatsIcon,
    description: 'Track who is using the Inspector Tool across daily, weekly and monthly windows.',
    section: 'main',
    parentKey: 'userManagement',
    featureGate: 'user_management',
  },
  editUser: {
    key: 'editUser',
    label: 'Edit User',
    path: '/admin/users',
    icon: PersonIcon,
    description: 'Update user roles, status and franchise partner access.',
    section: 'main',
    parentKey: 'userManagement',
    featureGate: 'user_management',
  },
  systemStatus: {
    key: 'systemStatus',
    label: 'System Status',
    path: '/admin/system/status',
    icon: MonitorHeartIcon,
    description: 'Monitor worker health, sync freshness and platform services.',
    section: 'system',
    featureGate: 'system_admin',
  },
  auditLogs: {
    key: 'auditLogs',
    label: 'Audit Logs',
    path: '/admin/system/audit-logs',
    icon: SecurityIcon,
    description: 'Review administrative activity and system change history.',
    section: 'system',
    featureGate: 'system_admin',
  },
  adminTools: {
    key: 'adminTools',
    label: 'Admin Tools',
    path: '/admin/system/admin-tools',
    icon: ConstructionIcon,
    description: 'Access administrative tools for support and platform maintenance.',
    section: 'system',
    featureGate: 'system_admin',
  },
  bulkOperations: {
    key: 'bulkOperations',
    label: 'Bulk Operations',
    path: '/admin/system/admin-tools/bulk-operations',
    icon: StorageIcon,
    description: 'Run bulk data operations across franchise partners for support and maintenance.',
    section: 'system',
    parentKey: 'adminTools',
    featureGate: 'system_admin',
  },
  pwxMasters: {
    key: 'pwxMasters',
    label: 'Poolwerx Master Lists',
    path: '/admin/system/pwx-masters',
    icon: ListAltIcon,
    description: 'View Poolwerx master lists consumed by exception checks across systems.',
    section: 'system',
    featureGate: 'system_admin',
  },
  bdm: {
    key: 'bdm',
    label: 'ROM/FBC Management',
    path: '/admin/system/bdm',
    icon: SupervisorAccountIcon,
    description: 'Manage Regional Operations Managers and Franchise Business Consultants.',
    section: 'system',
    featureGate: 'system_admin',
  },
  settings: {
    key: 'settings',
    label: 'Configuration',
    path: '/admin/system/configuration',
    icon: SettingsIcon,
    description: 'Manage administrative configuration, system lists and supporting setup.',
    section: 'system',
    featureGate: 'system_admin',
  },
  regionManagement: {
    key: 'regionManagement',
    label: 'Region Management',
    path: '/admin/system/regions',
    icon: PublicIcon,
    description: 'Manage regions and assign default ROM/FBC contacts.',
    section: 'system',
    featureGate: 'system_admin',
  },
  exceptionTypes: {
    key: 'exceptionTypes',
    label: 'Exception Setup',
    path: '/admin/system/exception-setup',
    icon: FactCheckIcon,
    description: 'Manage exception rules, classification, visibility and health scoring.',
    section: 'system',
    featureGate: 'system_admin',
  },
  integrations: {
    key: 'integrations',
    label: 'Integrations',
    path: '/admin/system/integrations',
    icon: HubIcon,
    description: 'Monitor and configure third-party integrations (Freshdesk, Confluence, Lightspeed, Skimmer).',
    section: 'system',
    featureGate: 'system_admin',
  },
  freshdeskIntegration: {
    key: 'freshdeskIntegration',
    label: 'Freshdesk',
    path: '/admin/system/integrations/freshdesk',
    icon: HubIcon,
    description: 'Read-only Freshdesk connection health and sync runs.',
    section: 'system',
    parentKey: 'integrations',
    featureGate: 'system_admin',
  },
  confluenceIntegration: {
    key: 'confluenceIntegration',
    label: 'Confluence',
    path: '/admin/system/integrations/confluence',
    icon: HubIcon,
    description: 'Read-only Confluence Cloud connection health and runs.',
    section: 'system',
    parentKey: 'integrations',
    featureGate: 'system_admin',
  },
}

export const getAdminPage = (key: AdminPageKey): AdminPageDefinition => adminPages[key]

/** Route the "System" breadcrumb crumb links to (redirects to configuration). */
const SYSTEM_ROOT_PATH = '/admin/system'

/**
 * Default breadcrumb trail for a page, derived from the registry:
 *   `Admin / [System] / [parent…] / <current>`
 * - `system` section pages get a clickable "System" crumb.
 * - `main` pages walk their `parentKey` chain.
 * The final crumb is the current page (no link); `titleOverride` replaces its
 * label for dynamic pages (e.g. a selected report name).
 *
 * Detail/edit pages that need a resolved entity name should pass an explicit
 * `breadcrumbs` prop to `AdminPageHeader` instead - the registry never guesses
 * entity names.
 */
export const registryBreadcrumbs = (
  key: AdminPageKey,
  titleOverride?: string,
): BreadcrumbItem[] => {
  const page = adminPages[key]
  const crumbs: BreadcrumbItem[] = [{ label: 'Admin', to: '/admin' }]

  if (page.section === 'system') {
    crumbs.push({ label: 'System', to: SYSTEM_ROOT_PATH })
  }

  // Walk the parent chain (nearest parent last), each as a link.
  const ancestors: AdminPageDefinition[] = []
  let parentKey = page.parentKey
  while (parentKey) {
    const parent = adminPages[parentKey]
    ancestors.unshift(parent)
    parentKey = parent.parentKey
  }
  ancestors.forEach((a) => crumbs.push({ label: a.label, to: a.path }))

  crumbs.push({ label: titleOverride ?? page.label })
  return crumbs
}

// ─── Side nav tree ───────────────────────────────────────────────────────────
// The whole side nav is described here so labels/icons/order/gates live in one
// place. `Navigation.tsx` only renders this (creating elements + injecting the
// dynamic Platform Reports category rows). Nav labels intentionally differ from
// page titles in places (e.g. "Configuration" -> Settings, "All FPs"), which is
// why links carry their own label/icon rather than always deriving from a page.

/** A single flyout row. `badgeKey` / `external` mirror the old nav behaviour. */
export interface AdminNavLink {
  label: string
  path: string
  icon: SvgIconComponent
  external?: boolean
  badgeKey?: 'feedbackNew'
}

/** Resolved nav group consumed by `Navigation.tsx` (icon always present). */
export interface AdminNavGroup {
  key: string
  label: string
  basePath: string
  icon: SvgIconComponent
  section: 'main' | 'system'
  featureGate?: string
  navOrder: number
  children: AdminNavLink[]
  /** Platform Reports: append the dynamic category rows at render time. */
  appendReportCategories?: boolean
}

// Group definition. Page-backed groups carry a `pageKey` and inherit `icon` +
// `featureGate` from the registry so those never drift from the page header;
// only the nav `label` (intentionally divergent - "Configuration" vs "Settings")
// and `basePath` (section root, e.g. /admin/data-sync vs the page's landing
// path) are declared locally. The System group has no backing page.
type AdminNavGroupDef = Omit<AdminNavGroup, 'icon' | 'featureGate'> & {
  pageKey?: AdminPageKey
  icon?: SvgIconComponent
  featureGate?: string
}

const adminNavDefs: AdminNavGroupDef[] = [
  {
    key: 'franchisePartners',
    pageKey: 'franchisePartners',
    label: 'Franchise Partners',
    basePath: '/admin/franchise-partners',
    section: 'main',
    navOrder: 10,
    children: [
      { label: 'All FPs', path: '/admin/franchise-partners', icon: GroupIcon },
      { label: 'System Connections', path: '/admin/system-connections', icon: LinkIcon },
      { label: 'Feedback', path: '/admin/feedback', icon: FeedbackIcon, badgeKey: 'feedbackNew' },
      { label: 'FP Directory', path: '/fp-directory', icon: OpenInNewIcon, external: true },
    ],
  },
  {
    key: 'dataExceptions',
    pageKey: 'dataExceptions',
    label: 'Data Exceptions',
    basePath: '/admin/data-exceptions',
    section: 'main',
    navOrder: 20,
    children: [
      { label: 'Overview', path: '/admin/data-exceptions', icon: DashboardIcon },
      { label: 'FP Grid', path: '/admin/data-exceptions/grid', icon: GridViewIcon },
      { label: 'Trending', path: '/admin/data-exceptions/trending', icon: WhatshotIcon },
      { label: 'Notes', path: '/admin/data-exceptions/notes', icon: StickyNote2Icon },
      { label: 'Reports', path: '/admin/data-exceptions/reports', icon: AssessmentIcon },
    ],
  },
  {
    key: 'healthScores',
    pageKey: 'healthScores',
    label: 'Health Scores',
    basePath: '/admin/health-scores',
    section: 'main',
    navOrder: 30,
    children: [
      { label: 'All FPs', path: '/admin/health-scores', icon: GroupIcon },
      { label: 'ANZ FPs', path: '/admin/health-scores?region=anz', icon: PublicIcon },
      { label: 'USA FPs', path: '/admin/health-scores?region=usa', icon: FlagIcon },
    ],
  },
  {
    key: 'platformReports',
    pageKey: 'platformReports',
    label: 'Platform Reports',
    basePath: '/admin/platform-reports',
    section: 'main',
    navOrder: 40,
    appendReportCategories: true,
    children: [
      { label: 'All Reports', path: '/admin/platform-reports', icon: AssessmentIcon },
    ],
  },
  {
    key: 'dataSync',
    pageKey: 'dataSync',
    label: 'Data Sync',
    basePath: '/admin/data-sync',
    section: 'main',
    navOrder: 50,
    children: [
      { label: 'Active Syncs', path: '/admin/data-sync/active', icon: PlayArrowIcon },
      { label: 'Sync History', path: '/admin/data-sync/history', icon: HistoryIcon },
      { label: 'Sync Tools', path: '/admin/data-sync/tools', icon: ConstructionIcon },
    ],
  },
  {
    key: 'userManagement',
    pageKey: 'userManagement',
    label: 'User Management',
    basePath: '/admin/users',
    section: 'main',
    navOrder: 60,
    children: [
      { label: 'All Users', path: '/admin/users', icon: PeopleIcon },
      { label: 'Evolution Team', path: '/admin/users/evo-team', icon: AdminPanelSettingsIcon },
      { label: 'PSC Team', path: '/admin/users/psc-team', icon: GroupsIcon },
      { label: 'FP Users', path: '/admin/users/fp-users', icon: PersonIcon },
      { label: 'User Activity', path: '/admin/users/activity', icon: QueryStatsIcon },
    ],
  },
  {
    key: 'system',
    label: 'System',
    basePath: '/admin/system',
    icon: SettingsIcon,
    featureGate: 'system_admin',
    section: 'system',
    navOrder: 90,
    children: [
      { label: 'System Status', path: '/admin/system/status', icon: MonitorHeartIcon },
      { label: 'Audit Logs', path: '/admin/system/audit-logs', icon: SecurityIcon },
      { label: 'Integrations', path: '/admin/system/integrations', icon: HubIcon },
      { label: 'Maintenance Tools', path: '/admin/system/admin-tools', icon: ConstructionIcon },
      { label: 'Configuration', path: '/admin/system/configuration', icon: SettingsIcon },
    ],
  },
]

/** Ordered side-nav groups. The single source consumed by `Navigation.tsx`.
 *  Page-backed groups inherit icon + feature gate from their registry page. */
export const buildAdminNav = (): AdminNavGroup[] =>
  [...adminNavDefs]
    .sort((a, b) => a.navOrder - b.navOrder)
    .map(({ pageKey, icon, featureGate, ...group }) => {
      const page = pageKey ? adminPages[pageKey] : undefined
      const resolvedIcon = icon ?? page?.icon
      if (!resolvedIcon) {
        throw new Error(`admin nav group "${group.key}" needs an icon or a pageKey`)
      }
      return { ...group, icon: resolvedIcon, featureGate: featureGate ?? page?.featureGate }
    })
